import {ReactNode, useCallback} from 'react';

import {retailerT} from '@/lib/types/retailer.types';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {
  BaseContextT,
  createAppContext,
  createAppHook,
  createAppProvider,
  createProviderConfig,
  useBaseContext,
} from '@/providers/components/baseProvider';

/*******************************************************************
 * Setup
 *******************************************************************/
// The table name and view name are used to create the context.
// The Filter's object is also returned from the createProviderConfig function if filters
// are used. E.g. const {table, columns, statsView, filters} = createProviderConfig({...});
const {table, columns, statsView} = createProviderConfig({
  table: 'Retailer',
  statsView: 'retailer_stats', // Optional*
  // Columns are optional, by default you get all the columns from the table (not referenced tables).
  // Ex:
  // columns: ["*"], // Default
  // columns: ['*', 'ReferencedTable(*)'],
  // columns: ['id', 'name', 'description'],
  //
  // Filters are optional, by default you get all the columns from the table (not referenced tables).
  // Ex:
  // filters: [
  //   {column: 'removed_by', operator: 'is', value: false},
  //   {not: {column: 'modified_by', operator: 'contains', value: 'some-uuid'}},
  //   {
  //     or: [
  //       {column: 'position_status', operator: 'is', value: 'success'},
  //       {column: 'position_status', operator: 'is', value: 'canceled'},
  //     ],
  //   },
  // ],
});

// Helper types to make the context more readable.
type TableT = typeof table;
type ColumnsT = typeof columns;
type StatsViewT = typeof statsView;

// Add any custom functions and properties type definitions here:
type CustomContextT = {
  // Properties:
  // Add custom properties here:
  // someProperty: string;
  // stats: string;
  // ...
  // Functions:
  getRetailer: (id: number) => Promise<retailerT>;
};

/*******************************************************************
 * Context
 *******************************************************************/
type RetailersContextT = BaseContextT<TableT, StatsViewT, ColumnsT, CustomContextT>;

// * IMPORTANT NOTE *
// It's important that the first part of this const, in this case the "Retailers"
// in "RetailersContext" matches the table name in the database exactly and must
// be proper-cased.
const RetailersContext = createAppContext<RetailersContextT>(table);

/*******************************************************************
 * Provider
 *******************************************************************/
type RetailersProviderPropsT = {
  children: ReactNode;
  // Add other custom props here:
  // someProp: string;
  // Very rarely use, so in most cases this type definition is not needed.
  // Just use {children: React.ReactNode} as the definition default.
};

// This is a React component in the end, so it must be proper cased.
const ContextProvider = ({children}: RetailersProviderPropsT) => {
  // The BaseContext is created for you automatically (stats is optional).
  const baseContext = useBaseContext<TableT, ColumnsT, StatsViewT>();
  const {isAdministrator} = useAuth();

  // Access the individual functions and properties from the base Context like this:
  // const {
  //   createData,
  //   updateData,
  //   deleteData,
  //   getData,
  //   stats,
  //   enabled,
  //   initialize,
  //   refreshData,
  //   refreshStats,
  //   sortData,
  //   searchData,
  //   pagination,
  // } = baseContext;
  //
  // You need to leave the baseContext as is, otherwise the provider will not work. But you can
  // deconstruct the baseContext as shown above for use in this provider.

  /*******************************************************************
   * Custom
   *******************************************************************/
  // Add custom functions, state, etc, here...

  /*******************************************************************
   * Gets a retailer record from the database.
   * @param {number} id - The ID of the retailer to get.
   * @returns {Promise<retailerT>} - The retailer object.
   * @throws {Error} - If there's an error in getting the retailer.
   ********************************************************************/
  const getRetailer = useCallback(
    async (id: number) => {
      if (!isAdministrator) throw new Error('Error getting retailer');
      try {
        const {data, error} = await SupabaseInstance.supabase.from('Retailer').select().eq('retailer_id', id).single();
        if (!data || error) throw new Error('Retailer not found');
        return data;
      } catch (error) {
        throw new Error((error as Error).message);
      }
    },
    [isAdministrator]
  );

  /*********************************************************************
   * Return the RetailersContext.Provider with the base context and
   * any additional functions and properties created above.
   ********************************************************************/
  return (
    <RetailersContext.Provider
      value={{
        ...baseContext,
        // Expose any custom functions, state, etc, here...
        getRetailer,
      }}
    >
      {children}
    </RetailersContext.Provider>
  );
};

// Add this to the AppProviders.tsx file
// e.g. <RetailersProvider>...</RetailersProvider>
export const RetailersProvider = createAppProvider({
  // The table and columns are required
  table,
  columns,
  statsView, // *Optional
  // Default state TanStack Query requests
  // Set whether the provider fetches data initially or not (optional, default: false)
  enabled: false, // *optional, default is false
  // The ContextProvider is the component that will wrap the app
  ContextProvider,
});

/*******************************************************************
 * Hook
 *******************************************************************/
// React hooks must follow the pattern: use[ProviderName]. In this case, useRetailers.
export const useRetailers = createAppHook<TableT, StatsViewT, ColumnsT, CustomContextT>(RetailersContext);
